import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Checkbox, Button } from '@mui/material';
import { motion } from 'framer-motion'
import { NavigateFunction, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form'



// COMPONENT 👇
import * as styles from './styles/login.style'
import { RouteImage } from '../../assets/routeImage';
import { ButtonCustom, TextFieldCustom } from '../../component/mui-custom/MuiCustom';
import { colors } from '../../constants/colors';
import { Authentication } from '../../api/auth/Authentication.api';
import Loading from '../../component/loading/Loading';
import { swalError } from '../../component/notification/swal';
import { getToken, setToken } from '../../utils/app.utils';

// STORE
import { useDispatch } from 'react-redux';
import { handleAddToken } from '../../store/slice/token/token.slice';
import { ResourcePathApi } from '../../constants/resource.pathapi';


type loginProps = {
    email: string;
    password: string;
}

const Login: React.FC = () => {
    const { t } = useTranslation();
    const token = getToken()
    const navigate: NavigateFunction = useNavigate();
    const dispatch = useDispatch();
    const [pathUrl, setPathUrl] = useState<any>(null);



    const { register, handleSubmit, formState: { errors } } = useForm<loginProps>({
        defaultValues: {
            email: "",
            password: ""
        }
    });
    const [isShowPassword, setIsShowPassword] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);


    const onSubmit = async (data: loginProps) => {
        setIsLoading(true)
        try {
            Authentication.login(data)
                .then(res => {
                    if (res.status === 200) {
                        // console.log(res.data)
                        setIsLoading(false)
                        const accessToken: string = res.data?.result?.accessToken
                        const refreshToken: string = res.data?.result?.refreshToken
                        const requireChangePassword = res.data?.result?.requireChangePassword
                        // console.log(requireChangePassword)
                        if (requireChangePassword) {
                            navigate('/reset-password', { state: { isAccept: true, token: accessToken } })
                        } else {
                            setIsLoading(false)
                            setToken(res.data?.result?.accessToken)
                            dispatch(handleAddToken({ accessToken, refreshToken }))
                            localStorage.setItem('REFRESH_TOKEN', refreshToken)
                            localStorage.setItem('DATE_TOKEN_EXPIRED', res.data?.result?.expireDate)
                            if (accessToken && refreshToken) {
                                window.location.href = '/auth/statistics'
                            }
                        }
                    } else {
                        console.log(res.data)
                    }
                })
                .catch(error => {
                    swalError(error?.response?.data?.description)
                    setIsLoading(false)
                })
        } catch (error) {
            console.log(error)
            setIsLoading(false)
        }
    }

    useEffect(() => {
        token && navigate('/auth/statistics')
    }, [])

    useEffect(() => {
        const fetchPath = async () => {
            const path = await ResourcePathApi();
            setPathUrl(path);
        }
        fetchPath();
    }, [])


    return (
        <styles.Container
            sx={{
                background: pathUrl && `url(${pathUrl.REACT_APP_BASE_API}/login-background) no-repeat`,
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            {isLoading && <Loading />}
            <styles.FormAuthentication onSubmit={handleSubmit(onSubmit)}>
                <styles.MainBox>
                    <styles.LoginBox
                        className='border-[1px] border-light'
                        initial={{ x: 200 }}
                        animate={{ x: 0 }}
                        transition={{ type: "spring", stiffness: 100, ease: "backInOut", bounce: 0.25 }}
                    >
                        <div className='flex justify-center items-center'>
                            <img width={120} height={120} src={pathUrl && `${pathUrl.REACT_APP_BASE_API}/app-logo`} alt="" />
                        </div>
                        <div className='mt-[36px]'>
                            <Typography variant='h4' component="h1">{t("ระบบสารสนเทศเพื่อการติดตามและประเมินผลโครงการ")}</Typography>
                            <Typography variant='h4' component="h1" >
                                {t(" (E-Monitoring and Evaluation)")}
                            </Typography>
                        </div>
                        <div className='mt-[36px] flex flex-col'>
                            <span>
                                <Typography component="p">
                                    {t("LOGIN.EMAIL_OR_USERNAME")}
                                </Typography>
                                <motion.div
                                    className='mt-[8px] flex flex-col gap-y-2'
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: .5 }}
                                >
                                    <TextFieldCustom
                                        type='email'
                                        {...register('email', { required: true })}
                                        InputProps={{
                                            startAdornment: (
                                                <img src={RouteImage.login_username_icon} />
                                            )
                                        }}
                                    />
                                    {errors.email && errors.email.type === 'required' && (
                                        <motion.span>
                                            <Typography sx={{ fontSize: "14px" }} className="text-danger" component="p" >
                                                {t("LOGIN.VALIDATE_LOGIN.USERNAME")}
                                            </Typography>
                                        </motion.span>
                                    )}
                                </motion.div>
                            </span>
                            <span className='mt-[16px]'>
                                <Typography component="p">
                                    {t("LOGIN.PASSWORD")}
                                </Typography>
                                <motion.div
                                    className='mt-[8px] flex flex-col gap-y-2'
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: .5 }}
                                >
                                    <TextFieldCustom
                                        {...register('password', { required: true })}
                                        type={isShowPassword ? 'text' : 'password'}
                                        InputProps={{
                                            startAdornment: (
                                                <img src={RouteImage.login_password_icon} />
                                            ),
                                            endAdornment: (
                                                <img className={`${isShowPassword ? 'opacity-1' : 'opacity-[.5]'} cursor-pointer`} onClick={() => setIsShowPassword(!isShowPassword)} src={RouteImage.login_eye_icon} />
                                            )
                                        }}
                                    />
                                    {errors.password && errors.password.type === 'required' && (
                                        <Typography sx={{ fontSize: "14px" }} className="text-danger" component="p" >
                                            {t("LOGIN.VALIDATE_LOGIN.PASSWORD")}
                                        </Typography>
                                    )}
                                </motion.div>
                            </span>
                            <span className='w-full flex justify-between items-center mt-[16px]'>
                                <motion.div className='flex justify-center items-center'>
                                    <Checkbox sx={{ color: colors.lampang_primary }} />
                                    <Typography component="p">
                                        {t("LOGIN.REMEMBER_PASSWORD")}
                                    </Typography>
                                </motion.div>
                                <motion.div className='cursor-pointer'>
                                    <Button>
                                        <Link to={'/forgot-password'}>
                                            <Typography sx={{ color: colors.lampang_text_secondary, '&:hover': { color: "#000" } }} component="p">
                                                {t("LOGIN.FORGOT_PASSWORD")}
                                            </Typography>
                                        </Link>
                                    </Button>
                                </motion.div>
                            </span>
                            <span className='w-full mt-[36px]'>
                                <ButtonCustom type='submit' variant='contained' sx={{
                                    height: "48px", '&:hover': {
                                        backgroundColor: colors.themeMainColor,
                                        transition: 'all .5s ease',
                                        opacity: .5
                                    }
                                }}
                                    fullWidth
                                >
                                    {t("LOGIN.LOGIN")}
                                </ButtonCustom>
                            </span>
                        </div>
                    </styles.LoginBox>
                </styles.MainBox>
            </styles.FormAuthentication>
        </styles.Container>
    )
}

export default Login
